import React, { useState } from 'react';
import { Box, Button, Modal, Typography } from '@mui/material';
import { DateRange, Range } from 'react-date-range';
import { enUS } from 'date-fns/locale';
import palette from '../../../theme/palette';
import { DEFAULT_DATE_FILTER_FORMAT, fDate } from '../../../utils/formatTime';
import CrossIconSvg from '../../../assets/svg-fc/CrossIconSvg';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './dateRangePicker.scss';

export interface DateRangePickerProps {
  value?: { startDate: Date | null; endDate: Date | null };
  placeholder?: string;
  label?: string;
  maxDaysRange?: number;
  disabled?: boolean;
  format?: string;
  onChange: (startDate: Date | null, endDate: Date | null) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select',
  label,
  maxDaysRange = 90,
  disabled = false,
  format = DEFAULT_DATE_FILTER_FORMAT,
}) => {
  const [isCalendarOpen, setCalendarOpen] = useState(false);
  const [isSelectingStartDate, setIsSelectingStartDate] = useState(false);
  const [selectionRange, setSelectionRange] = useState<Range[]>([
    {
      startDate: value?.startDate || new Date(),
      endDate: value?.endDate || new Date(),
      key: 'selection',
    },
  ]);

  const handleOpen = () => {
    if (disabled) return;
    setSelectionRange([
      {
        startDate: value?.startDate || new Date(),
        endDate: value?.endDate || new Date(),
        key: 'selection',
      },
    ]);
    setCalendarOpen(true);
  };

  const handleClose = () => {
    setCalendarOpen(false);
  };

  const handleApply = () => {
    const startDate = selectionRange[0]?.startDate || null;
    const endDate = selectionRange[0]?.endDate || null;
    onChange(startDate, endDate);
    setCalendarOpen(false);
  };

  const getDisplayValue = () => {
    if (!value?.startDate || !value?.endDate) {
      return <p className="dateRangePickerPlaceholder" style={{ color: palette.grey['800'], margin: 0 }}>{placeholder}</p>;
    }
    const start = fDate(value.startDate, format);
    const end = fDate(value.endDate, format);
    return `${start} - ${end}`;
  };

  return (
    <>
      <Box className="dateRangePickerInput" onClick={handleOpen}>
        <Typography className="dateRangePickerInputText">{getDisplayValue()}</Typography>
      </Box>
      {isCalendarOpen && (
        <Modal open={isCalendarOpen} onClose={handleClose} aria-labelledby="date-range-modal" className="dateRangeModal">
          <Box className="dateRangePickerContainer">
            <Box className="dateRangePickerHeader">
              <Typography className="dateRangePickerLabel">{label || 'Select Date Range'}</Typography>
              <Box className="crossIconContainer" onClick={handleClose}>
                <CrossIconSvg crosIconHandler={handleClose} size="12" />
              </Box>
            </Box>
            <DateRange
              editableDateInputs={false}
              onChange={(item) => setSelectionRange([item.selection])}
              moveRangeOnFirstSelection={false}
              showMonthAndYearPickers={false}
              ranges={selectionRange}
              locale={enUS}
              onRangeFocusChange={(focusedRange) => setIsSelectingStartDate(focusedRange[1] === 0)}
              color={palette.primary.main}
              rangeColors={[palette.primary.main, palette.primary.main]}
              retainEndDateOnFirstSelection={false}
            />
            <Button className="dateRangePickerButton" onClick={handleApply}>
              Apply
            </Button>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default DateRangePicker;
